import { action, observable, computed, runInAction } from 'mobx';
import { message } from 'antd';
import { TrainingProjectsService } from '../services';
import {
    TrainingProjectExampleDto,
    TrainingProjectExampleSourceType,
    TrainingProjectExampleOutputType,
    CreateEditTrainingProjectExampleFormValues
} from '../types';
import { TrainingProjectDashboardStore } from '../stores';

type SourceType = TrainingProjectExampleSourceType;

type OutputType = TrainingProjectExampleOutputType;

type CreateEditFormValues = CreateEditTrainingProjectExampleFormValues;

export default class TrainingProjectExampleModel {
    @observable
    sourceType: SourceType;

    @observable
    sourceValue: string;

    @observable
    documentName?: string;

    @observable
    outputType: OutputType;

    @observable
    outputValue: string;

    @observable
    explanation?: string;

    @observable
    updateDate?: Date;

    @observable
    updatedBy?: string;

    @observable
    isEditable: boolean = false;

    @observable
    isValid?: boolean;

    @observable
    validationErrors: string[] = [];

    readonly id: string;
    readonly trainingProjectId: string;
    readonly createDate: Date;
    readonly createdBy: string;

    constructor(
        private readonly store: TrainingProjectDashboardStore,
        private readonly service: TrainingProjectsService,
        data: TrainingProjectExampleDto
    ) {
        this.id = data.id;
        this.trainingProjectId = data.trainingProjectId;
        this.sourceType = data.sourceType;
        this.sourceValue = data.sourceValue;
        this.documentName = data.documentName;
        this.outputType = data.outputType;
        this.outputValue = data.outputValue;
        this.explanation = data.explanation;
        this.createdBy = data.createdBy;
        this.updatedBy = data.updatedBy;
        this.createDate = new Date(data.createDate);
        this.updateDate = data.updateDate ? new Date(data.updateDate) : undefined;
    }

    @computed
    get trainingProject() {
        return this.store.trainingProject;
    }

    @computed
    get sourceValueLabel() {
        if (this.sourceType === 'PackageId') {
            return (
                this.store.packageOptions.find(option => option.value === this.sourceValue)?.label ?? this.sourceValue
            );
        }

        return this.sourceValue;
    }

    @action.bound
    setEditable(isEditable: boolean) {
        this.isEditable = isEditable;
    }

    @action.bound
    async update(formValues: CreateEditFormValues) {
        if (!this.trainingProject) {
            return;
        }

        const resp = await this.service.updateTrainingProjectExample(
            this.trainingProject.projectId,
            this.trainingProjectId,
            this.id,
            formValues
        );

        if (!resp.isOk()) {
            message.error('Failed to update training project example');
            return false;
        }

        resp.map(data => this.updateData(data));

        return true;
    }

    @action.bound
    async delete() {
        if (!this.trainingProject) {
            return;
        }

        const resp = await this.service.deleteTrainingProjectExample(
            this.trainingProject.projectId,
            this.trainingProjectId,
            this.id
        );

        if (!resp.isOk()) {
            message.error('Failed to delete training project example');
            return;
        }

        this.store.removeTrainingProjectExample(this);
    }

    @action.bound
    updateValidationState(isValid: boolean | undefined, errors: string[]) {
        runInAction(() => {
            this.isValid = isValid;
            this.validationErrors = errors;
        });
    }

    updateData(data: TrainingProjectExampleDto) {
        runInAction(() => {
            this.sourceType = data.sourceType;
            this.sourceValue = data.sourceValue;
            this.documentName = data.documentName;
            this.outputType = data.outputType;
            this.outputValue = data.outputValue;
            this.explanation = data.explanation;
            this.updateDate = data.updateDate ? new Date(data.updateDate) : undefined;
            this.updatedBy = data.updatedBy;
        });

        this.store.refreshExamples();
    }
}
